// File: src/pages/OnboardingPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// --- 1. COMPONENT UI (Toast & Modal) - GIỮ NGUYÊN ---
const Toast = ({ message, type, onClose }) => {
    useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
    const isError = type === 'error';
    return (
        <div style={{
            position: 'fixed', bottom: '24px', left: '24px', zIndex: 10000,
            background: '#374151', color: 'white', padding: '12px 20px', borderRadius: '10px',
            display: 'flex', alignItems: 'center', gap: '10px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
            animation: 'slideUp 0.3s ease-out'
        }}>
            <span className="material-symbols-outlined" style={{ color: isError ? '#F87171' : '#4ADE80' }}>
                {isError ? 'error' : 'check_circle'}
            </span>
            {message}
        </div>
    );
};

const ExitModal = ({ onConfirm, onCancel }) => (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: 'white', padding: '24px', borderRadius: '16px', width: '320px', textAlign: 'center', animation: 'scaleUp 0.2s ease-out' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚪</div>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#111827', marginBottom: '8px' }}>Rời khỏi trang này?</h3>
            <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '24px' }}>Hồ sơ chưa hoàn tất. Nếu thoát, bạn sẽ bị đăng xuất.</p>
            <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={onCancel} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #D1D5DB', background: 'white', color: '#374151', fontWeight: '600', cursor: 'pointer' }}>Ở lại</button>
                <button onClick={onConfirm} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', background: '#EF4444', color: 'white', fontWeight: '600', cursor: 'pointer' }}>Đăng xuất</button>
            </div>
        </div>
    </div>
);

// Helper giải mã token
const parseJwt = (token) => {
    try {
        if (!token) return null;
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(decodeURIComponent(window.atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')));
    } catch (e) { return null; }
};

// --- 2. VALIDATION HELPERS ---
const REGEX = {
    NAME: /^[a-zA-ZÀÁÂÃÈÉÊÌÍÒÓÔÕÙÚĂĐĨŨƠàáâãèéêìíòóôõùúăđĩũơƯĂẠẢẤẦẨẪẬẮẰẲẴẶẸẺẼỀỀỂưăạảấầẩẫậắằẳẵặẹẻẽềềểỄỆỈỊỌỎỐỒỔỖỘỚỜỞỠỢỤỦỨỪễệỉịọỏốồổỗộớờởỡợụủứừỬỮỰỲỴÝỶỸửữựỳỵýỷỹ\s]{2,}$/,
    PHONE: /^(0[3|5|7|8|9])+([0-9]{8})\b/,
    TAX: /^[0-9]{10}$|^[0-9]{13}$/,
    NUMBER_ONLY: /^\d+$/
};

// --- 3. LOGIC CHÍNH ---

export default function OnboardingPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    
    // State Flow: 
    // Step 1: Điền thông tin (Info)
    // Step 2: Nhập MST (Business Only) - (Đã xóa bước OTP)
    const [step, setStep] = useState(1); 
    
    // State UI
    const [toast, setToast] = useState(null);
    const [showExitModal, setShowExitModal] = useState(false);

    // State Data
    const [role, setRole] = useState(null);
    const [isRoleFixed, setIsRoleFixed] = useState(false);
    const [formData, setFormData] = useState({ fullName: '', phoneNumber: '' });
    
    // Business Data
    const [taxCode, setTaxCode] = useState('');
    const [businessInfo, setBusinessInfo] = useState(null);

    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

    // --- A. INIT & LOGIC ĐIỀU HƯỚNG ---
    useEffect(() => {
        // Chặn nút Back
        window.history.pushState(null, null, window.location.pathname);
        const handlePopState = () => {
            window.history.pushState(null, null, window.location.pathname); 
            setShowExitModal(true); 
        };
        window.addEventListener('popstate', handlePopState);
        
        const token = localStorage.getItem('skillmatch_token');
        if (!token) { navigate('/login'); return; }
        
        const decoded = parseJwt(token);
        if (decoded?.user) {
            // ✅ QUAN TRỌNG: Nếu chưa verify -> Đuổi về Login ngay
            if (!decoded.user.isVerified) {
                localStorage.removeItem('skillmatch_token');
                navigate('/login');
                return;
            }

            if (decoded.user.role) { 
                setRole(decoded.user.role); 
                setIsRoleFixed(true); 
            }
            
            // Vào thẳng Step 1 vì đã verify rồi
            setStep(1);
        }

        return () => window.removeEventListener('popstate', handlePopState);
    }, [navigate]);

    const handleConfirmExit = () => {
        localStorage.removeItem('skillmatch_token');
        navigate('/login');
    };

    // --- B. HELPERS ---
    const showToast = (msg, type = 'success') => setToast({ message: msg, type });
    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    // --- C. XỬ LÝ LOGIC ---

    // 1. Xử lý "Tiếp tục" ở Step 1 (Info)
    const handleSubmitInfo = async (e) => {
        if (e) e.preventDefault();
        
        // Validation
        if (!role) return showToast("Vui lòng chọn vai trò!", 'error');
        const name = formData.fullName.trim();
        const phone = formData.phoneNumber.trim();

        if (!name) return showToast("Vui lòng nhập họ tên!", 'error');
        if (!REGEX.NAME.test(name)) return showToast("Họ tên không hợp lệ", 'error');
        if (!phone) return showToast("Vui lòng nhập số điện thoại!", 'error');
        if (!REGEX.PHONE.test(phone)) return showToast("SĐT không hợp lệ", 'error');

        // Logic điều hướng
        if (role === 'business') {
            setStep(2); // Sang bước MST (Step 2)
        } else {
            await saveProfileToDB(); // Lưu luôn nếu là Candidate
        }
    };

    // 2. Tra cứu MST (Step 2 - Business)
    const handleLookupMST = async () => {
        const cleanTax = taxCode.trim();
        if (!cleanTax) return showToast("Vui lòng nhập Mã số thuế", 'error');
        if (!REGEX.TAX.test(cleanTax)) return showToast("MST không hợp lệ", 'error');

        setLoading(true);
        const token = localStorage.getItem('skillmatch_token');
        try {
            const res = await fetch(`${apiBaseUrl}/api/auth/lookup-mst`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
                body: JSON.stringify({ taxCode: cleanTax })
            });
            const data = await res.json();
            if (res.ok) {
                setBusinessInfo(data.data);
                showToast("Tìm thấy thông tin doanh nghiệp!");
            } else {
                setBusinessInfo(null);
                throw new Error(data.message);
            }
        } catch (err) { showToast(err.message, 'error'); }
        finally { setLoading(false); }
    };

    // 3. Lưu vào Database (Gọi khi Candidate submit info hoặc Business submit MST)
    const saveProfileToDB = async () => {
        setLoading(true);
        const token = localStorage.getItem('skillmatch_token');
        const finalData = {
            fullName: formData.fullName.trim(),
            phoneNumber: formData.phoneNumber.trim(),
            role,
            ...(role === 'business' && businessInfo ? {
                taxCode: businessInfo.taxCode,
                companyName: businessInfo.companyName,
                address: businessInfo.address
            } : {})
        };

        try {
            const res = await fetch(`${apiBaseUrl}/api/auth/update-profile`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
                body: JSON.stringify(finalData)
            });
            const data = await res.json();
            if (res.ok) {
                if (data.token) localStorage.setItem('skillmatch_token', data.token);
                showToast("Hoàn tất! Đang chuyển hướng...");
                setTimeout(() => {
                    navigate(role === 'business' ? '/business/dashboard' : '/');
                }, 1000);
            } else throw new Error(data.message);
        } catch (err) { showToast(err.message, 'error'); }
        finally { setLoading(false); }
    };

    // --- D. RENDER ---
    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 font-sans p-4 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full z-0 pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
                <div className="absolute top-[-10%] right-[-10%] w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md border border-gray-100 z-10 relative transition-all">
                
                <div className="text-center mb-8">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl mx-auto flex items-center justify-center text-white text-2xl font-bold mb-4 shadow-lg">S</div>
                    <h1 className="text-2xl font-bold text-gray-900">
                        {step === 1 ? "Hoàn thiện hồ sơ" : "Xác thực Doanh nghiệp"}
                    </h1>
                    <p className="text-gray-500 text-sm mt-2">
                        {step === 1 ? "Vui lòng cập nhật thông tin." : 
                         "Nhập Mã số thuế để xác minh danh tính."}
                    </p>
                </div>

                {/* STEP 1: INFO FORM */}
                {step === 1 && (
                    <form onSubmit={handleSubmitInfo} className="space-y-5 animate-fadeIn">
                        {!isRoleFixed && (
                            <div className="grid grid-cols-2 gap-4">
                                {['candidate', 'business'].map((r) => (
                                    <div key={r} onClick={() => setRole(r)}
                                        className={`cursor-pointer p-4 rounded-xl border-2 text-center transition-all ${role === r ? 'border-blue-600 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-blue-300'}`}>
                                        <div className="text-2xl mb-1">{r === 'candidate' ? '🎓' : '🏢'}</div>
                                        <div className={`text-sm font-bold ${role === r ? 'text-blue-700' : 'text-gray-600'}`}>{r === 'candidate' ? 'Ứng viên' : 'Doanh nghiệp'}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Họ và Tên</label>
                                <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Nguyễn Văn A" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Số điện thoại</label>
                                <input type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 outline-none" placeholder="0912 xxx xxx" />
                            </div>
                        </div>
                        <button type="submit" disabled={loading} className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg transition-all flex justify-center items-center gap-2">
                            {loading ? 'Đang xử lý...' : 'Tiếp tục'} <span className="material-symbols-outlined text-sm">arrow_forward</span>
                        </button>
                    </form>
                )}

                {/* STEP 2: MST (Only Business) */}
                {step === 2 && role === 'business' && (
                    <div className="animate-fadeIn space-y-6">
                        <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 flex gap-3 items-start">
                            <div className="bg-blue-100 p-2 rounded-lg text-blue-600">
                                <span className="material-symbols-outlined text-xl">domain_verification</span>
                            </div>
                            <div>
                                <p className="text-sm font-bold text-blue-900">Xác thực Doanh nghiệp</p>
                                <p className="text-xs text-blue-700 mt-1">
                                    Nhập Mã số thuế để hệ thống tự động tải thông tin chính xác từ Tổng cục Thuế.
                                </p>
                            </div>
                        </div>

                        {/* Form Tra Cứu */}
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Mã Số Thuế</label>
                            <div className="flex gap-2">
                                <input 
                                    type="text" 
                                    value={taxCode} 
                                    onChange={(e) => {
                                        if (e.target.value && !REGEX.NUMBER_ONLY.test(e.target.value)) return;
                                        setTaxCode(e.target.value);
                                        setBusinessInfo(null);
                                    }} 
                                    onKeyDown={(e) => e.key === 'Enter' && handleLookupMST()}
                                    className="flex-1 px-4 py-3 rounded-xl border border-gray-300 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 outline-none font-mono tracking-wide text-lg" 
                                    placeholder="010xxxxxxx" 
                                />
                                <button 
                                    onClick={handleLookupMST} 
                                    disabled={loading || !taxCode} 
                                    className="px-6 py-3 bg-gray-900 text-white rounded-xl font-bold hover:bg-black transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                >
                                    {loading ? <span className="material-symbols-outlined animate-spin">sync</span> : 'Kiểm tra'}
                                </button>
                            </div>
                        </div>

                        {/* Kết quả Tra Cứu */}
                        {businessInfo && (
                            <div className="bg-white p-5 rounded-xl border-2 border-green-100 shadow-sm animate-slideDown relative overflow-hidden">
                                <div className="absolute top-0 right-0 bg-green-100 text-green-700 px-3 py-1 rounded-bl-lg text-xs font-bold">
                                    Đã tìm thấy
                                </div>
                                <div className="mb-4">
                                    <p className="text-xs text-gray-400 uppercase font-bold mb-1">Tên Công Ty</p>
                                    <h3 className="text-lg font-bold text-gray-900 leading-tight">{businessInfo.companyName}</h3>
                                </div>
                                <div className="flex gap-4">
                                    <div className="flex-1">
                                        <p className="text-xs text-gray-400 uppercase font-bold mb-1">Mã Số Thuế</p>
                                        <p className="font-mono text-gray-700 font-medium">{businessInfo.taxCode}</p>
                                    </div>
                                </div>
                                <div className="mt-4 pt-4 border-t border-gray-100">
                                    <p className="text-xs text-gray-400 uppercase font-bold mb-1">Địa chỉ trụ sở</p>
                                    <p className="text-sm text-gray-600">{businessInfo.address}</p>
                                </div>
                            </div>
                        )}

                        {/* Nút Xác nhận cuối cùng */}
                        <button 
                            onClick={saveProfileToDB} 
                            disabled={!businessInfo || loading} 
                            className={`w-full py-4 font-bold rounded-xl shadow-lg transition-all flex justify-center items-center gap-2 text-lg
                            ${businessInfo 
                                ? 'bg-blue-600 hover:bg-blue-700 text-white transform hover:scale-[1.02]' 
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
                        >
                            {loading ? 'Đang xử lý...' : 'Xác nhận & Hoàn tất'}
                            {!loading && businessInfo && <span className="material-symbols-outlined">check_circle</span>}
                        </button>
                    </div>
                )}
            </div>

            {/* UI COMPONENTS */}
            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
            {showExitModal && <ExitModal onConfirm={handleConfirmExit} onCancel={() => setShowExitModal(false)} />}
            
            <style>{`
                @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
                @keyframes scaleUp { from { transform: scale(0.9); opacity: 0; } to { transform: scale(1); opacity: 1; } }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes slideDown { from { opacity: 0; height: 0; } to { opacity: 1; height: auto; } }
                .animate-fadeIn { animation: fadeIn 0.4s ease-out; }
                .animate-slideDown { animation: slideDown 0.3s ease-out; }
                .animate-blob { animation: blob 7s infinite; }
                @keyframes blob { 0% { transform: translate(0px, 0px) scale(1); } 33% { transform: translate(30px, -50px) scale(1.1); } 66% { transform: translate(-20px, 20px) scale(0.9); } 100% { transform: translate(0px, 0px) scale(1); } }
                .animation-delay-2000 { animation-delay: 2s; }
            `}</style>
        </div>
    );
}